<template>
  <div>
    <div class="mode-toggle panzoom-ignore">
      <v-btn-toggle v-model="liveChatMp4Data.editorMode" mandatory dense>
        <v-btn value="tree" small>Tree</v-btn>
        <v-btn value="json" small>JSON</v-btn>
      </v-btn-toggle>
    </div>

    <LiveChatMp4V2JsonMode v-if="liveChatMp4Data.editorMode === 'json'" :live-chat-data="liveChatMp4Data" />

    <template v-else>
      <div v-if="flowError" class="flow-error panzoom-ignore">{{ flowError }}</div>
      <div class="zoom-actions">
        <v-btn @click="zoomIn">+</v-btn>
        <v-btn class="ml-2" @click="zoomOut">-</v-btn>
        <v-btn class="ml-2" @click="reset">
          <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
        </v-btn>
      </div>

      <div class="panzoom-parent">
        <div ref="panzoom">
          <div class="d-flex justify-center">
            <div class="chatbot-data">
              <div class="title mb-2">{{ $t("shortcodes.live_chat_mp4.agent_media") }}</div>
              <VaVideoFileInput
                v-model="liveChatMp4Data.waitingLoopMp4Url"
                :file-id.sync="liveChatMp4Data.waitingLoopMp4FileId"
                class="mb-3 panzoom-ignore"
                :label="$t('shortcodes.live_chat_mp4.waiting_loop_mp4_url')" />
              <div v-if="liveChatMp4Data.waitingLoopMp4Url" class="d-flex align-center mb-3">
                <v-text-field
                  class="panzoom-ignore mr-3"
                  v-model.number="liveChatMp4Data.waitingLoopVideoOffsetX"
                  type="number"
                  :label="$t('shortcodes.live_chat_mp4.video_offset_x')"
                  persistent-placeholder
                  placeholder="0"></v-text-field>
                <v-text-field
                  class="panzoom-ignore"
                  v-model.number="liveChatMp4Data.waitingLoopVideoOffsetY"
                  type="number"
                  :label="$t('shortcodes.live_chat_mp4.video_offset_y')"
                  persistent-placeholder
                  placeholder="0"></v-text-field>
              </div>
              <VaMediaObjectFileInput
                :label="$t('shortcodes.live_chat_mp4.avatar')"
                class="mb-3 panzoom-ignore"
                :item-id="liveChatMp4Data.botAvatar.imageID"
                @update:photo="updateAvatar($event)" />
              <v-text-field
                v-model.number="liveChatMp4Data.maxAvatarSize"
                class="mb-4 panzoom-ignore"
                type="number"
                :label="$t('shortcodes.live_chat_mp4.max_avatar_size')"
                persistent-placeholder
                placeholder="240"></v-text-field>

              <v-select
                v-model="liveChatMp4Data.videoPreloadMode"
                class="mb-4 panzoom-ignore"
                :items="videoPreloadModeItems"
                :label="$t('shortcodes.live_chat_mp4.video_preload_mode')"
                persistent-placeholder></v-select>

              <VaColorPicker
                class="mb-4 panzoom-ignore"
                :title="'Action items background color'"
                v-model="liveChatMp4Data.actionItemBackgroundColor"></VaColorPicker>
              <VaColorPicker
                class="mb-4 panzoom-ignore"
                :title="$t('shortcodes.live_chat_mp4.message_background')"
                v-model="liveChatMp4Data.messageBackgroundColor"></VaColorPicker>

              <div class="title mb-2 mt-4">{{ $t("shortcodes.live_chat_mp4.top_pills_and_typing") }}</div>
              <v-text-field
                v-model="liveChatMp4Data.waitingPillLabel"
                class="mb-3 panzoom-ignore"
                :label="$t('shortcodes.live_chat_mp4.waiting_pill_label')"
                persistent-placeholder
                placeholder="waiting after you"></v-text-field>
              <v-text-field
                v-model.number="liveChatMp4Data.typingSpeed"
                class="mb-3 panzoom-ignore"
                type="number"
                :label="$t('shortcodes.live_chat_mp4.typing_speed')"
                persistent-placeholder
                placeholder="50"></v-text-field>

              <v-text-field
                v-model="liveChatMp4Data.loaderMessage"
                class="mb-4 panzoom-ignore"
                persistent-placeholder
                :label="$t('shortcodes.live_chat_mp4.loader_message')"
                placeholder="Mary"></v-text-field>

              <div class="d-flex justify-center">
                <v-btn :color="'primary'" class="mr-2" v-if="displayAddMessageBtn" @click="addMessage">
                  {{ $t("shortcodes.live_chat_auto.add_message") }}
                </v-btn>
              </div>
            </div>
          </div>
          <div class="message-tree">
            <LiveChatMp4V2Tree :message-tree="liveChatMp4Data.messages"></LiveChatMp4V2Tree>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import LiveChatMp4V2Tree from "@/components/shortcodes/livechatMp4V2/LiveChatMp4V2Tree.vue";
  import LiveChatMp4V2JsonMode from "@/components/shortcodes/livechatMp4V2/LiveChatMp4V2JsonMode.vue";
  import Panzoom from "@panzoom/panzoom";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import VaVideoFileInput from "@/components/custom/VaVideoFileInput";
  import cloneDeep from "lodash/cloneDeep";
  import { v4 as uuidv4 } from "uuid";
  import {
    MESSAGE_MP4,
    LIVE_CHAT_MP4_ROOT_DEFAULTS,
    MESSAGE_MP4_DEFAULTS,
    LIVE_CHAT_MP4_ACTION_DEFAULTS,
    LIVE_CHAT_MP4_ACTION_ITEM_DEFAULTS,
    VIDEO_PRELOAD_MODE_OPTIONS,
  } from "@/components/shortcodes/livechatMp4V2/constant";
  import definedAbility from "@/ability";
  import { convertAssetsPathToAbsoluteLinks, toAbsoluteAssetUrl } from "@/utility/convertAssetsPaths";
  import { traverseTree } from "@/utility/traverseTree";
  import { syncTelHrefErrors } from "@/utility/validateLiveChatTelHrefs";
  import { flowErrorMessage, normalizeIds } from "@/components/shortcodes/livechatV2/treeGraph";

  const origin = window.location.origin;

  export default {
    name: "LiveChatMp4V2Dialog",
    components: { LiveChatMp4V2Tree, LiveChatMp4V2JsonMode, VaColorPicker, VaVideoFileInput },
    data() {
      return {
        ability: definedAbility(),
        panzoomInstance: null,
        panzoomWheelParent: null,
        liveChatMp4Data: { ...cloneDeep(LIVE_CHAT_MP4_ROOT_DEFAULTS), editorMode: "tree" },
      };
    },
    computed: {
      displayAddMessageBtn() {
        return this.liveChatMp4Data.messages && this.liveChatMp4Data.messages.length === 0;
      },
      videoPreloadModeItems() {
        return VIDEO_PRELOAD_MODE_OPTIONS.map(opt => ({ value: opt.value, text: this.$t(opt.textKey) }));
      },
      flowError() {
        return flowErrorMessage(this.liveChatMp4Data.messages);
      },
    },
    watch: {
      liveChatMp4Data: {
        handler(newValue) {
          this.updateValue(newValue);
        },
        deep: true,
      },
      "liveChatMp4Data.editorMode"(mode) {
        if (mode !== "json") {
          this.$nextTick(() => this.initPanzoom());
        }
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.initPanzoom();
    },
    beforeDestroy() {
      this.teardownPanzoom();
    },
    methods: {
      initPanzoom() {
        if (!this.$refs.panzoom) return;
        // Re-runs on each Tree/JSON toggle, so the previous instance and listener are dropped first.
        this.teardownPanzoom();

        this.panzoomInstance = Panzoom(this.$refs.panzoom, {
          canvas: true,
          excludeClass: "panzoom-ignore",
          handleStartEvent: () => {},
        });

        this.$refs.panzoom.style.height = `${window.innerHeight - 24}px`;

        this.panzoomWheelParent = this.$refs.panzoom.parentElement;
        this.panzoomWheelParent.addEventListener("wheel", this.onPanzoomWheel);
      },
      teardownPanzoom() {
        if (this.panzoomWheelParent) {
          this.panzoomWheelParent.removeEventListener("wheel", this.onPanzoomWheel);
          this.panzoomWheelParent = null;
        }
        if (this.panzoomInstance) {
          this.panzoomInstance.destroy();
          this.panzoomInstance = null;
        }
      },
      onPanzoomWheel(event) {
        if (!this.panzoomInstance) return;
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });

          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      },
      addMessage() {
        const message = cloneDeep(MESSAGE_MP4);
        message.id = uuidv4();
        this.liveChatMp4Data.messages.push(message);
      },
      reset() {
        if (this.panzoomInstance) this.panzoomInstance.reset();
      },
      zoomIn() {
        if (this.panzoomInstance) this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        if (this.panzoomInstance) this.panzoomInstance.zoomOut();
      },
      updateAvatar(value) {
        if (value) {
          this.liveChatMp4Data.botAvatar.imageID = value["@id"];
          this.liveChatMp4Data.botAvatar.imageURL = value.contentUrl || "";
        } else {
          this.liveChatMp4Data.botAvatar.imageID = "";
          this.liveChatMp4Data.botAvatar.imageURL = "";
        }
      },
      updateValue(val) {
        const data = { ...val };
        window.parent.postMessage(
          {
            mceAction: "updateLiveChatData",
            data,
          },
          origin,
        );
      },
      syncTelHrefErrors() {
        syncTelHrefErrors(this.liveChatMp4Data.messages, this);
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (!message.data || typeof message.data !== "object") return;

          if (message.data.type === "telHrefValidationFailed") {
            this.syncTelHrefErrors();

            return;
          }

          if (message.data.type === "sendData") {
            const { data } = message.data;

            const normalized = { ...cloneDeep(LIVE_CHAT_MP4_ROOT_DEFAULTS), ...data };
            if (!normalized.messages) normalized.messages = [];
            if (!normalized.botAvatar) normalized.botAvatar = { imageID: "", imageURL: "" };
            normalized.editorMode = normalized.editorMode === "json" ? "json" : "tree";

            normalized.waitingLoopMp4Url = toAbsoluteAssetUrl(normalized.waitingLoopMp4Url);
            if (normalized.botAvatar?.imageURL) {
              normalized.botAvatar.imageURL = toAbsoluteAssetUrl(normalized.botAvatar.imageURL);
            }

            traverseTree(normalized.messages, node => {
              if (node.content) node.content = convertAssetsPathToAbsoluteLinks(node.content);
              if (node.text) node.text = convertAssetsPathToAbsoluteLinks(node.text);
              if (node.value) node.value = convertAssetsPathToAbsoluteLinks(node.value);
              if (node.mp4Url) node.mp4Url = toAbsoluteAssetUrl(node.mp4Url);
            });
            // Id-less nodes are dropped by the graph (blank canvas).
            normalizeIds(normalized.messages);

            this.liveChatMp4Data = normalized;

            traverseTree(this.liveChatMp4Data.messages, node => {
              if (node.messageType === "message") {
                Object.keys(MESSAGE_MP4_DEFAULTS).forEach(key => {
                  if (node[key] === undefined) this.$set(node, key, MESSAGE_MP4_DEFAULTS[key]);
                });
              } else if (node.messageType === "action") {
                Object.keys(LIVE_CHAT_MP4_ACTION_DEFAULTS).forEach(key => {
                  if (node[key] === undefined) this.$set(node, key, LIVE_CHAT_MP4_ACTION_DEFAULTS[key]);
                });
              } else if (node.messageType === "actionItem") {
                Object.keys(LIVE_CHAT_MP4_ACTION_ITEM_DEFAULTS).forEach(key => {
                  if (node[key] === undefined) this.$set(node, key, LIVE_CHAT_MP4_ACTION_ITEM_DEFAULTS[key]);
                });
              }
            });
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";

  .zoom-actions {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
  }

  .mode-toggle {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 102;
  }

  .flow-error {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 101;
    max-width: 520px;
    padding: 8px 12px;
    border: 1px solid get-color("error");
    border-radius: 6px;
    background: get-color("element-bg-100");
    color: get-color("error");
    @include text-body-2-bold();
  }

  .title {
    color: get-color("subtitle");
    @include heading-4();
  }

  .chatbot-data {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    background: get-color("element-bg-100");
    padding: 5px 10px;
    max-width: 600px;
    min-width: 600px;
  }

  .message-tree {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);

    .message-item {
      border: 1px dashed get-color("primary");
      border-radius: 5px;
      background: get-color("element-bg-100");
      padding: 5px 10px;

      &__settings-wrapper {
        display: flex;
        align-items: center;
      }

      .message-item__settings {
        &--delay {
          max-width: 100px;
        }

        &--human {
          margin-left: 10px;
        }

        &--no-icon {
          margin-left: 10px;
        }
      }
    }
  }
</style>
